import React, { useCallback, useEffect, useState } from "react";

import { DriveDropzone } from "./components/DriveDropzone";
import { DriveHeader } from "./components/DriveHeader";
import { DriveInputs } from "./components/DriveInputs";
import { useBatchUpload } from "./hooks/useBatchUpload";
import { useFileUpload } from "./hooks/useFileUpload";
import { useFolderUpload } from "./hooks/useFolderUpload";

import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { THOUSAND, THREE, TWO } from "@/constants/constants";
import { useDrive } from "@/context/DriveContext";
import { BatchFileItem, BatchFolderItem } from "@/interfaces/project.interface";
import { BatchUploadManager } from "@/pages/drive/BatchUploadManager";
import SampleDirLoader from "@/pages/drive/SampleFolderLoader";
import { customToast } from "@/utils/custom-toast";

interface DriveUploadManagerProps {
  onFolderAdd?: () => void;
  children?: React.ReactNode;
  isManager?: boolean;
}

const DriveUploadManager: React.FC<DriveUploadManagerProps> = ({
  onFolderAdd,
  children,
  isManager = false,
}) => {
  const {
    type,
    ID: id,
    viewType,
    setViewType,
    handleSearch,
    activeFolderTarget,
    setActiveFolderTarget,
  } = useDrive();

  // State
  const [dropzoneFiles, setDropzoneFiles] = useState<File[]>([]);
  const [batchFiles, setBatchFiles] = useState<BatchFileItem[]>([]);
  const [batchFolders, setBatchFolders] = useState<BatchFolderItem[]>([]);
  const [showBatchUpload, setShowBatchUpload] = useState(false);
  const [isFileInputLoading, setIsFileInputLoading] = useState(false);
  const [isFolderInputLoading, setIsFolderInputLoading] = useState(false);
  const [isDropzoneLoading, setIsDropzoneLoading] = useState(false);

  // Custom hooks
  const { uploadSingleFile, validateFile, createBatchFileItem } =
    useFileUpload();
  const {
    uploadBatch,
    isUploading,
    uploadProgress,
    uploadError,
    unsupportedFiles,
    resetUpload,
  } = useBatchUpload();
  const { processFileSystemEntries, processWebkitDirectoryFiles } =
    useFolderUpload();

  // Helper function to reset loading states
  const resetAllLoadingStates = useCallback((): void => {
    setIsFileInputLoading(false);
    setIsFolderInputLoading(false);
    setIsDropzoneLoading(false);
  }, []);

  // Handle single file selection
  const handleFileSelect = useCallback(
    async (file: File): Promise<void> => {
      if (!validateFile(file)) {
        resetAllLoadingStates();

        return;
      }

      setIsFileInputLoading(true);
      setShowBatchUpload(true);
      setBatchFiles([createBatchFileItem(file)]);
      setBatchFolders([]);

      try {
        await uploadSingleFile(
          file,
          (progress) => {
            setBatchFiles((prev) => prev.map((f) => ({ ...f, progress })));
          },
          (status) => {
            setBatchFiles((prev) => prev.map((f) => ({ ...f, status })));

            if (status === "success") {
              setTimeout(() => {
                setShowBatchUpload(false);
                setBatchFiles([]);
                resetUpload();
                resetAllLoadingStates();
              }, THREE * THOUSAND);
            } else if (status === "error") {
              setTimeout(() => {
                setShowBatchUpload(false);
                setBatchFiles([]);
                resetUpload();
                resetAllLoadingStates();
              }, TWO * THOUSAND);
            }
          },
        );
      } catch (error) {
        setTimeout(() => {
          setShowBatchUpload(false);
          setBatchFiles([]);
          resetUpload();
          resetAllLoadingStates();
        }, TWO * THOUSAND);
      }
    },
    [
      validateFile,
      createBatchFileItem,
      uploadSingleFile,
      resetUpload,
      resetAllLoadingStates,
    ],
  );

  // Handle folder selection from input
  const handleFolderSelect = useCallback(
    async (fileList: FileList): Promise<void> => {
      setIsFolderInputLoading(true);

      const { files, folders } = processWebkitDirectoryFiles(
        Array.from(fileList),
      );

      if (files.length > 0) {
        setShowBatchUpload(true);
        setBatchFiles(files);
        setBatchFolders(folders);

        try {
          await uploadBatch(files, folders, undefined, (updatedFiles) => {
            setBatchFiles(updatedFiles);

            // Check if all files are completed (success or error)
            const allCompleted = updatedFiles.every(
              (f) => f.status === "success" || f.status === "error",
            );

            if (allCompleted) {
              const hasSuccessfulUploads = updatedFiles.some(
                (f) => f.status === "success",
              );

              setTimeout(
                () => {
                  setShowBatchUpload(false);
                  setBatchFiles([]);
                  setBatchFolders([]);
                  resetUpload();
                  resetAllLoadingStates();
                },
                hasSuccessfulUploads ? THREE * THOUSAND : TWO * THOUSAND,
              );
            }
          });
        } catch (error) {
          setTimeout(() => {
            setShowBatchUpload(false);
            setBatchFiles([]);
            setBatchFolders([]);
            resetUpload();
            resetAllLoadingStates();
          }, TWO * THOUSAND);
        }
      } else {
        customToast.error("No valid files found in the selected folder");
        resetAllLoadingStates();
      }
    },
    [
      processWebkitDirectoryFiles,
      uploadBatch,
      resetUpload,
      resetAllLoadingStates,
    ],
  );

  // Handle drag and drop folder upload
  const handleFolderDrop = useCallback(
    async (entries: FileSystemEntry[]): Promise<void> => {
      setIsDropzoneLoading(true);
      const { files, folders } = await processFileSystemEntries(entries);

      if (files.length > 0) {
        setShowBatchUpload(true);
        setBatchFiles(files);
        setBatchFolders(folders);

        try {
          await uploadBatch(files, folders, undefined, (updatedFiles) => {
            setBatchFiles(updatedFiles);

            // Check if all files are completed (success or error)
            const allCompleted = updatedFiles.every(
              (f) => f.status === "success" || f.status === "error",
            );

            if (allCompleted) {
              const hasSuccessfulUploads = updatedFiles.some(
                (f) => f.status === "success",
              );

              setTimeout(
                () => {
                  setShowBatchUpload(false);
                  setBatchFiles([]);
                  setBatchFolders([]);
                  resetUpload();
                  resetAllLoadingStates();
                },
                hasSuccessfulUploads ? THREE * THOUSAND : TWO * THOUSAND,
              );
            }
          });
        } catch (error) {
          setTimeout(() => {
            setShowBatchUpload(false);
            setBatchFiles([]);
            setBatchFolders([]);
            resetUpload();
            resetAllLoadingStates();
          }, TWO * THOUSAND);
          // Error handled by uploadBatch
        }
      } else {
        customToast.error("No valid files found to upload");
        setTimeout(() => {
          setShowBatchUpload(false);
          resetAllLoadingStates();
        }, TWO * THOUSAND);
      }
    },
    [processFileSystemEntries, uploadBatch, resetUpload, resetAllLoadingStates],
  );

  // Handle dropzone file changes
  const handleDropzoneFilesChange = useCallback((files: File[]): void => {
    setDropzoneFiles(files);
  }, []);

  // Process dropzone files when they change
  useEffect(() => {
    if (dropzoneFiles.length > 0) {
      setIsDropzoneLoading(true);

      const validFiles = dropzoneFiles
        .filter(validateFile)
        .map(createBatchFileItem);

      if (validFiles.length > 0) {
        setShowBatchUpload(true);
        setBatchFiles(validFiles);
        setBatchFolders([]);

        void uploadBatch(validFiles, [], undefined, (updatedFiles) => {
          setBatchFiles(updatedFiles);

          // Check if all files are completed (success or error)
          const allCompleted = updatedFiles.every(
            (f) => f.status === "success" || f.status === "error",
          );

          if (allCompleted) {
            const hasSuccessfulUploads = updatedFiles.some(
              (f) => f.status === "success",
            );

            setTimeout(
              () => {
                setShowBatchUpload(false);
                setBatchFiles([]);
                resetUpload();
                resetAllLoadingStates();
              },
              hasSuccessfulUploads ? THREE * THOUSAND : TWO * THOUSAND,
            );
          }
        });
      } else {
        resetAllLoadingStates();
      }

      setDropzoneFiles([]);
    }
  }, [
    dropzoneFiles,
    validateFile,
    createBatchFileItem,
    uploadBatch,
    resetUpload,
    resetAllLoadingStates,
  ]);

  // Handle global drag events
  useEffect(() => {
    const handleGlobalDragEnd = (): void => {
      if (activeFolderTarget) {
        setActiveFolderTarget(null);
      }
    };

    const handleDragLeave = (e: DragEvent): void => {
      if (
        e.target === document ||
        e.target === document.documentElement ||
        e.target === document.body
      ) {
        setActiveFolderTarget(null);
      }
    };

    document.addEventListener("dragend", handleGlobalDragEnd);
    document.addEventListener("drop", handleGlobalDragEnd);
    document.addEventListener("dragleave", handleDragLeave);

    return (): void => {
      document.removeEventListener("dragend", handleGlobalDragEnd);
      document.removeEventListener("drop", handleGlobalDragEnd);
      document.removeEventListener("dragleave", handleDragLeave);
    };
  }, [activeFolderTarget, setActiveFolderTarget]);

  const cancelBatchUpload = useCallback((): void => {
    setShowBatchUpload(false);
    setBatchFiles([]);
    setBatchFolders([]);
    resetAllLoadingStates();
    resetUpload();
  }, [resetUpload, resetAllLoadingStates]);

  return (
    <>
      {/* Upload interface (only if not manager) */}
      {!isManager && (
        <>
          <DriveDropzone
            files={dropzoneFiles}
            onFilesChange={handleDropzoneFilesChange}
            onFolderDrop={handleFolderDrop}
            isLoading={isDropzoneLoading}
          />
        </>
      )}

      <DriveHeader
        isManager={isManager}
        type={type}
        viewType={viewType}
        setViewType={setViewType}
        onSearch={handleSearch}
      >
        {!isManager && (
          <div className='flex items-center space-x-2'>
            <DriveInputs
              onFileSelect={handleFileSelect}
              onFolderSelect={handleFolderSelect}
              onAddFolder={onFolderAdd}
              isFileInputLoading={isFileInputLoading}
              isFolderInputLoading={isFolderInputLoading}
            />

            {type === driveTypes.PROJECT_REGISTER && (
              <SampleDirLoader
                variant='secondary'
                type={type}
                id={id}
                className='w-full sm:w-auto'
              />
            )}
          </div>
        )}
      </DriveHeader>

      {/* Render children */}
      {children}

      {/* Render batch upload manager */}
      {showBatchUpload && (
        <BatchUploadManager
          files={batchFiles}
          folders={batchFolders}
          totalProgress={uploadProgress}
          isUploading={isUploading}
          error={uploadError}
          onCancel={cancelBatchUpload}
          unsupportedFiles={unsupportedFiles}
        />
      )}
    </>
  );
};

export default DriveUploadManager;
